import { useCallback, useEffect, useMemo } from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
  Link as RouterLink,
} from 'react-router-dom';

/**
 * expo-router's navigation API, over React Router.
 *
 * The screens are ports of their native counterparts and navigate the way those
 * do — `router.push('/tickets/' + id)`, `useLocalSearchParams()`,
 * `<Redirect href="/welcome" />`. Reproducing that surface here means a screen
 * can be diffed against its mobile original and the difference is presentation
 * only, never routing. It is about forty lines, and underneath it is ordinary
 * React Router: real URLs, real history, real back button, deep links that work
 * when pasted.
 *
 * Route paths are IDENTICAL to the native app's, minus expo-router's grouping
 * folders — `(app)/(tabs)/home.tsx` and `/home` were always the same route.
 */

export type Href = string | { pathname: string; params?: Record<string, string | number | undefined> };

/**
 * Resolve expo-router's href shapes into a URL.
 *
 * `{ pathname: '/articles/[slug]', params: { slug } }` fills the bracket
 * segments and puts anything left over in the query string, which is exactly
 * what expo-router does with the same input.
 */
export function resolveHref(href: Href): string {
  if (typeof href === 'string') return href;
  const params = { ...(href.params ?? {}) };
  const path = href.pathname.replace(/\[([^\]]+)\]/g, (_m, key: string) => {
    const value = params[key];
    delete params[key];
    return encodeURIComponent(String(value ?? ''));
  });
  const query = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => encodeURIComponent(k) + '=' + encodeURIComponent(String(v)));
  return query.length ? path + '?' + query.join('&') : path;
}

export type AppRouter = {
  push: (href: Href) => void;
  replace: (href: Href) => void;
  back: () => void;
  canGoBack: () => boolean;
  navigate: (href: Href) => void;
  /** expo-router's "pop to the top of the stack". Here: go home. */
  dismissAll: () => void;
  /**
   * expo-router's setParams: merge into the current route's query string in
   * place (no new history entry). `undefined` or '' removes a key. This is what
   * lets a filtered list survive a refresh, a shared link, and the trip to a
   * product page and back.
   */
  setParams: (params: Record<string, string | number | undefined>) => void;
};

export function useRouter(): AppRouter {
  const navigate = useNavigate();
  const location = useLocation();

  return useMemo<AppRouter>(
    () => ({
      push: (href) => navigate(resolveHref(href)),
      replace: (href) => navigate(resolveHref(href), { replace: true }),
      navigate: (href) => navigate(resolveHref(href)),
      // `idx` is React Router's own position in the history stack. At 0 there is
      // nothing of ours behind us — going back would leave the app entirely, so
      // the header's back arrow falls through to the tab root instead.
      back: () => {
        const idx = (location.state as { idx?: number } | null)?.idx;
        if (typeof idx === 'number' && idx <= 0) navigate('/home', { replace: true });
        else navigate(-1);
      },
      canGoBack: () => window.history.length > 1,
      dismissAll: () => navigate('/home'),
      setParams: (params) => {
        // Read the live URL, not the render's: two calls in one tick must not
        // each start from the same stale query string.
        const search = new URLSearchParams(window.location.search);
        for (const [key, value] of Object.entries(params)) {
          if (value === undefined || value === '') search.delete(key);
          else search.set(key, String(value));
        }
        const qs = search.toString();
        if (qs === window.location.search.replace(/^\?/, '')) return;
        navigate({ search: qs ? `?${qs}` : '' }, { replace: true, state: location.state });
      },
    }),
    [navigate, location.state],
  );
}

/**
 * Path params and query string in one object, like expo-router's.
 * Everything is a string — the same coercion the native router applies.
 */
export function useLocalSearchParams<T extends Record<string, string | undefined> = Record<string, string | undefined>>(): T {
  const params = useParams();
  const [search] = useSearchParams();

  return useMemo(() => {
    const out: Record<string, string | undefined> = { ...params };
    search.forEach((value, key) => {
      out[key] = value;
    });
    return out as T;
  }, [params, search]);
}

/** expo-router's declarative redirect. */
export function Redirect({ href }: { href: Href }) {
  return <Navigate to={resolveHref(href)} replace />;
}

/** expo-router's <Link>, rendering a real anchor so middle-click and "open in
 *  new tab" behave the way a fan expects on the web. */
export function Link({
  href,
  children,
  className,
  style,
}: {
  href: Href;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <RouterLink to={resolveHref(href)} className={className} style={style}>
      {children}
    </RouterLink>
  );
}

/**
 * Runs `effect` when the screen is entered, like expo-router's `useFocusEffect`.
 *
 * The web stack unmounts a screen when you navigate away — there is no
 * "backgrounded but mounted" state for a route to come back into focus from — so
 * mount IS focus here, and the returned cleanup runs on blur exactly as it does
 * on native.
 */
export function useFocusEffect(effect: () => void | (() => void)) {
  const location = useLocation();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stable = useCallback(effect, [location.key]);
  useEffect(stable, [stable]);
}
